import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
} from 'react';

import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styleColors from '../colors.styles';
import TemplateItem from './TemplateItem';
import SpinningSpark from '../shared/components/SpinningSpark';
import TemplatesFilters from './TemplatesFilters';
import {
  debounce,
  fetchFiltersAndTemplates,
  endActiveTemplatesForTopicOrSubtopic,
  getActiveTemplateCount,
  getDisplayedTemplates,
} from './Templates.service';
import TemplateConfirmModal from './TemplateConfirmModal';
import { AlertsContext } from '../shared/alerts/Alerts.context';
import {
  buildTemplateFiltersSearchParams,
  buildTemplatesListSearch,
  parseTemplateFiltersFromSearchParams,
  persistTemplatesListSearch,
  templateFiltersEqual,
} from './templateFilters.utils';

const SEARCH_URL_SYNC_DELAY_MS = 300;

export default function Templates() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(() =>
    parseTemplateFiltersFromSearchParams(searchParams)
  );
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [templatesToEnd, setTemplatesToEnd] = useState({});
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get('search') || ''
  );
  const postTypes = [
    { label: 'Pre-written', value: 'pw' },
    { label: 'Fill-in-the-Blank', value: 'fitb' },
    { label: 'Product Link', value: 'pl' },
  ];
  const [filterValues, setFilterValues] = useState({ postTypes });

  const { showAlert } = useContext(AlertsContext);

  const activeCounts = useMemo(() => {
    if (!filterValues.topics) return {};
    const counts = {};
    filterValues.topics.forEach((topic) => {
      counts[`topic-${topic.id}`] = getActiveTemplateCount(
        templates,
        topic.id,
        'topic'
      );
      topic.subtopics?.forEach((subtopic) => {
        counts[`subtopic-${subtopic.id}`] = getActiveTemplateCount(
          templates,
          subtopic.id,
          'subtopic'
        );
      });
    });
    return counts;
  }, [templates, filterValues.topics]);

  const displayedTemplates = useMemo(
    () => getDisplayedTemplates(templates, appliedFilters, searchQuery),
    [templates, appliedFilters, searchQuery]
  );

  const syncFiltersToUrl = useCallback(
    (filters, query) => {
      const nextParams = buildTemplateFiltersSearchParams(filters, query);
      const nextSearch = nextParams.toString();

      if (nextSearch !== searchParams.toString()) {
        setSearchParams(nextParams, { replace: true });
      }

      persistTemplatesListSearch(buildTemplatesListSearch(filters, query));
    },
    [searchParams, setSearchParams]
  );

  const debouncedSyncFiltersToUrl = useMemo(
    () =>
      debounce(
        (filters, query) => syncFiltersToUrl(filters, query),
        SEARCH_URL_SYNC_DELAY_MS
      ),
    [syncFiltersToUrl]
  );

  useEffect(() => {
    const filters = parseTemplateFiltersFromSearchParams(searchParams);
    const query = searchParams.get('search') || '';

    setAppliedFilters((current) =>
      templateFiltersEqual(current, filters) ? current : filters
    );
    setSearchQuery((current) => (current === query ? current : query));
  }, [searchParams]);

  useEffect(() => {
    fetchFiltersAndTemplatesData();
  }, []);

  useEffect(
    () => () => {
      debouncedSyncFiltersToUrl.cancel();
    },
    [debouncedSyncFiltersToUrl]
  );

  const fetchFiltersAndTemplatesData = async () => {
    setLoadingTemplates(true);
    try {
      const { topics: fetchedTopics, templates: fetchedTemplates } =
        await fetchFiltersAndTemplates();
      setFilterValues((prev) => ({ ...prev, topics: fetchedTopics }));
      setTemplates(fetchedTemplates);
    } catch (error) {
      console.error(error);
      showAlert(
        'Failed to load templates and topics. Please try again.',
        'error'
      );
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleFilterChange = (query, newFilters = appliedFilters) => {
    const filtersToProcess = { ...newFilters };
    if (filtersToProcess.postType === 'pl') {
      filtersToProcess.topic = '';
      filtersToProcess.subtopic = '';
    }

    const filtersChanged = !templateFiltersEqual(
      filtersToProcess,
      appliedFilters
    );
    const searchChanged = query !== searchQuery;

    setAppliedFilters(filtersToProcess);
    setSearchQuery(query);

    if (filtersChanged) {
      debouncedSyncFiltersToUrl.cancel();
      syncFiltersToUrl(filtersToProcess, query);
      return;
    }

    if (searchChanged) {
      debouncedSyncFiltersToUrl(filtersToProcess, query);
    }
  };

  const navigateToTemplateForm = (pathname) => {
    debouncedSyncFiltersToUrl.cancel();
    syncFiltersToUrl(appliedFilters, searchQuery);

    const listSearch = buildTemplatesListSearch(appliedFilters, searchQuery);
    const isCreate = pathname.endsWith('/new');

    persistTemplatesListSearch(listSearch);
    navigate({
      pathname,
      search: isCreate ? listSearch : '',
      state: {
        templatesListSearch: listSearch,
      },
    });
  };

  const handleDeleteTopic = (topicId) => {
    if (!topicId) return;
    const activeCount = getActiveTemplateCount(templates, topicId, 'topic');
    const subtopics =
      filterValues.topics
        ?.find((topic) => String(topic.id) === String(topicId))
        ?.subtopics?.map((sub) => ({
          name: sub.name,
          qty: getActiveTemplateCount(templates, sub.id, 'subtopic'),
        })) || [];
    let topicName = null;
    const foundTopic = filterValues.topics?.find(
      (topic) => String(topic.id) === String(topicId)
    );
    if (foundTopic) topicName = foundTopic.name;
    setTemplatesToEnd({
      id: topicId,
      type: 'topic',
      qty: activeCount,
      name: topicName,
      subtopics,
    });
    setConfirmModalOpen(true);
  };

  const handleDeleteSubtopic = (subtopicId) => {
    if (!subtopicId) return;
    const activeCount = getActiveTemplateCount(
      templates,
      subtopicId,
      'subtopic'
    );
    let subtopicName = null;
    filterValues.topics?.forEach((topic) => {
      const sub = topic.subtopics?.find(
        (item) => String(item.id) === String(subtopicId)
      );
      if (sub) subtopicName = sub.name;
    });
    setTemplatesToEnd({
      id: subtopicId,
      type: 'subtopic',
      qty: activeCount,
      name: subtopicName,
    });
    setConfirmModalOpen(true);
  };

  const selectedTopic = filterValues.topics?.find(
    (topic) => String(topic.id) === String(appliedFilters.topic)
  );
  const selectedSubtopic = selectedTopic?.subtopics?.find(
    (subtopic) => String(subtopic.id) === String(appliedFilters.subtopic)
  );
  let endTarget = null;
  if (selectedSubtopic) {
    endTarget = {
      label: 'End subtopic',
      onClick: () => handleDeleteSubtopic(selectedSubtopic.id),
    };
  } else if (selectedTopic) {
    endTarget = {
      label: 'End topic',
      onClick: () => handleDeleteTopic(selectedTopic.id),
    };
  }

  const handleConfirmDelete = async () => {
    if (!templatesToEnd.qty) {
      setConfirmModalOpen(false);
      setTemplatesToEnd({});
      return;
    }

    try {
      await endActiveTemplatesForTopicOrSubtopic(
        templates,
        templatesToEnd.id,
        templatesToEnd.type
      );
      showAlert('Templates ended successfully!', 'success');
      fetchFiltersAndTemplatesData();
    } catch (error) {
      console.error('Failed to end templates:', error);
      showAlert('Failed to end templates. Please try again.', 'error');
    } finally {
      setConfirmModalOpen(false);
      setTemplatesToEnd({});
    }
  };

  return (
    <Stack spacing={3} sx={styles.page}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Typography sx={styles.title}>Templates</Typography>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {endTarget && (
            <Button
              type="button"
              variant="outlined"
              sx={styles.endButton}
              onClick={endTarget.onClick}
            >
              {endTarget.label}
            </Button>
          )}
          <TextField
            value={searchQuery}
            placeholder="Search"
            onChange={(event) =>
              handleFilterChange(event.target.value, appliedFilters)
            }
            size="small"
            sx={styles.search}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={styles.searchIcon} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            disabled={!filterValues.topics}
            sx={styles.createButton}
            onClick={() => navigateToTemplateForm('/templates/new')}
          >
            New template
          </Button>
        </Stack>
      </Stack>
      {confirmModalOpen && (
        <TemplateConfirmModal
          templatesData={templatesToEnd}
          isOpen={confirmModalOpen}
          closeModal={() => setConfirmModalOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems="flex-start"
        spacing={3}
      >
        <TemplatesFilters
          filterValues={filterValues}
          appliedFilters={appliedFilters}
          searchQuery={searchQuery}
          onFilterChange={handleFilterChange}
          activeCounts={activeCounts}
        />
        {loadingTemplates ? (
          <Box display="flex" justifyContent="center" flex={1} width="100%">
            <SpinningSpark isSpinning />
          </Box>
        ) : (
          <Box sx={styles.templatesContainer}>
            {displayedTemplates.map((template) => (
              <TemplateItem
                key={template.id}
                template={template}
                onClick={() =>
                  navigateToTemplateForm(`/templates/${template.id}`)
                }
              />
            ))}
          </Box>
        )}
      </Stack>
    </Stack>
  );
}

const styles = {
  page: {
    padding: '8px 4px 24px',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#041E42',
    lineHeight: 1.2,
  },
  search: {
    width: 240,
    borderRadius: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      height: 40,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E3E8EF',
    },
  },
  searchIcon: {
    color: '#8B9BB0',
    fontSize: 20,
  },
  endButton: {
    borderRadius: '8px',
    height: 40,
    textTransform: 'none',
    fontWeight: 600,
    borderColor: '#E3E8EF',
    color: '#D32F2F',
    whiteSpace: 'nowrap',
    '&:hover': {
      color: '#D32F2F',
      borderColor: '#E3E8EF',
      backgroundColor: 'transparent',
    },
  },
  createButton: {
    backgroundColor: styleColors.base.walmart_blue,
    borderRadius: '8px',
    height: 40,
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    paddingLeft: '14px',
    paddingRight: '16px',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: styleColors.base.walmart_blue,
      boxShadow: 'none',
    },
  },
  templatesContainer: {
    display: 'grid',
    flex: 1,
    width: '100%',
    minWidth: 0,
    gap: '20px',
    gridTemplateColumns: {
      xs: 'repeat(1, 1fr)',
      lg: 'repeat(2, 1fr)',
      xl: 'repeat(3, 1fr)',
    },
  },
};
